/**
 * The scratch rate law. Pure — this is the file the scratch tests pin.
 *
 * Semantics (the scene's contract):
 *  - Angular VELOCITY, not angular position, controls the signed rate. A hand
 *    that holds the platter still is a hand asking for rate ~0, which is what
 *    "held" sounds like on a turntable.
 *  - Reverse motion is a negative rate. The worklet plays backwards; nothing
 *    else in Web Audio can.
 *  - RELEASE eases back to 1 — the platter is let go and the motor wins. That
 *    ease is deliberately slower than the ease under the hand.
 *  - The CUT gesture mutes rapidly. It is a gate, not a rate: cutting does not
 *    change where the playhead is going.
 */

export const RATE_MIN = -8;
export const RATE_MAX = 8;
/** Velocity clamp before scaling — hands can spin faster than is musical. */
export const TARGET_CLAMP = 6;
/** rad/s → rate. Roughly one revolution per second = 1x. */
export const VEL_TO_RATE = 0.52;
/**
 * Walk time constants, in SECONDS: firm under the hand, loose on release.
 * These were per-call coefficients (0.6 / 0.16), which made the walk's speed a
 * function of frame rate — release took twice as long at 30 fps as at 60.
 * The values are the τ that reproduce the old feel at 60 fps exactly:
 * 1 − e^(−(1/60)/τ) = coefficient.
 */
export const TAU_HELD = 0.0182;
export const TAU_RELEASE = 0.0957;
/** Release snaps to exactly 1 inside this window, so it never crawls. */
export const SNAP_EPSILON = 0.005;

/** Per-frame interpolation factor for a time constant, honest about dt. */
export const kFor = (tau: number, dt: number): number => 1 - Math.exp(-Math.max(dt, 1e-3) / tau);

const TAU = Math.PI * 2;

/** Shortest signed angular difference, unwrapped across the +/-pi seam. */
export function unwrapDelta(prev: number, next: number): number {
  let d = next - prev;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return d;
}

/** rad/s. dt is clamped away from zero so a duplicated frame cannot divide by 0. */
export function angularVelocity(prev: number, next: number, dt: number): number {
  return unwrapDelta(prev, next) / Math.max(dt, 1e-3);
}

const clamp = (lo: number, hi: number, v: number): number => (v < lo ? lo : v > hi ? hi : v);

/** The target the rate is walking toward while the hand is on the platter. */
export function targetRate(velocity: number): number {
  return clamp(-TARGET_CLAMP, TARGET_CLAMP, velocity * VEL_TO_RATE);
}

/**
 * One step of the rate walk.
 *   held    → interpolate toward the velocity-derived target with TAU_HELD
 *   release → interpolate toward 1 with TAU_RELEASE, snapping inside SNAP_EPSILON
 * dt makes the walk frame-rate independent: the same physical gesture traces
 * the same rate curve at 30 and at 60 fps.
 */
export function stepRate(current: number, velocity: number, dt: number, held: boolean): number {
  if (held) {
    const t = targetRate(velocity);
    return clamp(RATE_MIN, RATE_MAX, current + (t - current) * kFor(TAU_HELD, dt));
  }
  const next = current + (1 - current) * kFor(TAU_RELEASE, dt);
  return Math.abs(next - 1) < SNAP_EPSILON ? 1 : clamp(RATE_MIN, RATE_MAX, next);
}

/** Convenience: the whole per-frame update from two angles and a dt. */
export function rateFromAngles(
  current: number,
  prevAngle: number,
  angle: number,
  dt: number,
  held: boolean,
): number {
  return stepRate(current, held ? angularVelocity(prevAngle, angle, dt) : 0, dt, held);
}
